import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiTruck,
  FiExternalLink,
  FiCopy,
  FiCheck,
  FiMapPin,
  FiAlertCircle,
  FiEdit3,
} from "react-icons/fi";
import { MdOutlineLocalShipping, MdOutlineVerified } from "react-icons/md";
import { TbPackageExport } from "react-icons/tb";
import axios from "../../../Utils/axiosInstance";
import ConfirmActionModal from "../Quotations/ConfirmActionModal";

const formatAddress = (addr) => {
  if (!addr) return "—";
  if (typeof addr === "string") return addr;
  return [
    addr.addressLine1 || addr.address,
    addr.addressLine2,
    addr.city,
    addr.state,
    addr.pincode ? `PIN: ${addr.pincode}` : null,
  ]
    .filter(Boolean)
    .join(", ");
};

const fmtDateTime = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const getReturnTrackingSteps = (status) => [
  { label: "Return Approved", completed: true },
  { label: "Dispatched", completed: ["Dispatched", "Delivered"].includes(status) },
  { label: "In Transit", completed: ["Dispatched", "Delivered"].includes(status) },
  { label: "Delivered to Supplier", completed: status === "Delivered" },
];

const ReturnDispatchCard = ({
  orderId,
  orderNumber,
  quotationNumber,
  stage,
  onDispatchSuccess,
}) => {
  const navigate = useNavigate();
  const [returnDoc, setReturnDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [awbNumber, setAwbNumber] = useState("");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copiedAWB, setCopiedAWB] = useState(false);
  const [showEditTracking, setShowEditTracking] = useState(false);
  const [updatingTracking, setUpdatingTracking] = useState(false);

  const targetOrderNumber =
    orderNumber ||
    (typeof returnDoc?.orderId === "object" ? returnDoc?.orderId?.orderNumber : null);
  const targetOrderId =
    orderId ||
    (typeof returnDoc?.orderId === "object" ? returnDoc?.orderId?._id : returnDoc?.orderId);
  const caseId = returnDoc?.uniqueCaseId || returnDoc?._id;

  const handleNavigateToReturnDispatch = () => {
    const params = new URLSearchParams();
    if (targetOrderNumber) {
      params.set("orderNumber", targetOrderNumber);
    }
    if (targetOrderId) {
      params.set("highlight", targetOrderId);
    } else if (caseId) {
      params.set("highlight", caseId);
    }
    params.set("section", "return-dispatch");

    navigate(`/returns?${params.toString()}`, {
      state: {
        scrollTo: "return-dispatch",
        orderNumber: targetOrderNumber,
        orderId: targetOrderId,
        caseId: caseId,
      },
    });
  };

  const fetchReturnDetails = useCallback(async () => {
    if (!orderId) return;
    try {
      setLoading(true);
      const res = await axios.get(`/return/by-order/${orderId}`);
      if (res.data?.success && res.data?.data) {
        const doc = res.data.data;
        setReturnDoc(doc);
        if (doc.returnDispatch?.AWB_Number) {
          setAwbNumber(doc.returnDispatch.AWB_Number);
        }
        if (doc.returnDispatch?.trackingUrl) {
          setTrackingUrl(doc.returnDispatch.trackingUrl);
        }
      }
    } catch (err) {
      console.warn("Return details not available for order:", orderId, err?.message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchReturnDetails();
  }, [fetchReturnDetails]);

  const handleCopyAWB = (awb) => {
    if (!awb) return;
    navigator.clipboard.writeText(awb);
    setCopiedAWB(true);
    toast.success("AWB Number copied to clipboard!");
    setTimeout(() => setCopiedAWB(false), 2000);
  };

  const handleDispatch = async () => {
    if (!returnDoc?._id) return;
    try {
      setSubmitting(true);
      const payload = {};
      if (awbNumber.trim()) payload.AWB_Number = awbNumber.trim();
      if (trackingUrl.trim()) payload.trackingUrl = trackingUrl.trim();

      const res = await axios.patch(`/return/${returnDoc._id}/dispatch`, payload);
      if (res.data?.success) {
        toast.success("Return marked as dispatched successfully! ✅ Admin and supplier notified.");
        setConfirmModalOpen(false);
        await fetchReturnDetails();
        if (onDispatchSuccess) onDispatchSuccess(res.data.data);
      } else {
        toast.error(res.data?.message || "Failed to mark return as dispatched");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong while initiating dispatch");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateTracking = async () => {
    if (!returnDoc?._id) return;
    try {
      setUpdatingTracking(true);
      const payload = {
        AWB_Number: awbNumber.trim(),
        trackingUrl: trackingUrl.trim(),
      };
      const res = await axios.patch(
        `/return/${returnDoc._id}/update-dispatch-details`,
        payload
      );
      if (res.data?.success) {
        toast.success("Return tracking details updated successfully ✅");
        setShowEditTracking(false);
        await fetchReturnDetails();
      } else {
        toast.error(res.data?.message || "Failed to update tracking details");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update tracking");
    } finally {
      setUpdatingTracking(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 animate-pulse">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gray-200" />
          <div className="space-y-1.5 flex-1">
            <div className="h-4 bg-gray-200 rounded w-1/4" />
            <div className="h-3 bg-gray-100 rounded w-1/2" />
          </div>
        </div>
        <div className="h-20 bg-gray-50 rounded-xl" />
      </div>
    );
  }

  if (!returnDoc) return null;

  const dispatchStatus = returnDoc.returnDispatch?.status || "Not_Initiated";
  const isDispatched = dispatchStatus === "Dispatched" || dispatchStatus === "Delivered";
  const isDelivered = dispatchStatus === "Delivered";
  const isPartial = returnDoc.returnType === "Partial" || stage === "Partial_Order_Returned";

  const packetsToReturn =
    returnDoc.partialReturnPackets ||
    returnDoc.packSpecification?.numberOfPacksToBeReturned ||
    "—";
  const weightPerPack = returnDoc.packSpecification?.weightOfEachPack;
  const unitOfWeight = returnDoc.packSpecification?.unitOfWeight || "Kg";

  const pickupAddr =
    returnDoc.returnDispatch?.pickUpAddress ||
    returnDoc.orderId?.address;
  const deliveryAddr =
    returnDoc.returnDispatch?.deliveryAddress ||
    returnDoc.quotationId?.pickUpAddress;

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 relative group">
        {/* Subtle accent border at top */}
        <div className="h-1 bg-gradient-to-r from-[#772037] via-[#CC234F] to-amber-500" />

        {/* ══ CARD HEADER ══ */}
        <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50/90 to-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#772037]/15 to-[#CC234F]/15 text-[#772037] flex items-center justify-center flex-shrink-0 shadow-sm">
              <TbPackageExport className="text-xl" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-gray-800 text-sm sm:text-base">
                  {isPartial ? "Partial Return Dispatch" : "Return Order Dispatch"}
                </h3>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 font-semibold">
                  {returnDoc.uniqueCaseId || `#${returnDoc._id?.slice(-6)}`}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {isDispatched
                  ? "Track your return shipment to the supplier warehouse"
                  : "Dispatch return items back to the supplier"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isDispatched && (
              <button
                type="button"
                onClick={handleNavigateToReturnDispatch}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-[#772037] bg-[#772037]/10 hover:bg-[#772037]/15 border border-[#772037]/20 transition cursor-pointer"
                title="Go to Return Dispatch section"
              >
                <span>Dispatch Section</span>
                <FiExternalLink className="text-[11px]" />
              </button>
            )}
            {isDelivered ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                Delivered to Supplier
              </span>
            ) : isDispatched ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                In Transit (Dispatched)
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 animate-pulse">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                Action Required: Dispatch Package
              </span>
            )}
          </div>
        </div>

        {/* ══ CONTENT BODY ══ */}
        <div className="p-5 space-y-5">
          {/* ─ NOTIFICATION / INSTRUCTION CALLOUT ─ */}
          {!isDispatched ? (
            <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50/70 to-orange-50/40 p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <FiAlertCircle className="text-base" />
                </div>
                <div className="flex-1">
                  <h4 className="text-xs font-bold text-amber-900 uppercase tracking-wide">
                    Return Approved by Dispute Resolution
                  </h4>
                  <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                    Please pack the {isPartial ? "agreed partial return items" : "return items"}{" "}
                    securely and dispatch them to the supplier. Once dispatched manually, click{" "}
                    <strong>“Initiate Dispatch”</strong>in the Return page . Admin and supplier will be
                    notified automatically.
                  </p>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={handleNavigateToReturnDispatch}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-gradient-to-r from-[#772037] to-[#CC234F] hover:opacity-90 text-white font-bold text-xs shadow-sm transition active:scale-95 cursor-pointer"
                    >
                      <span>Go to Return Dispatch</span>
                      <FiExternalLink className="text-xs" />
                    </button>
                    {targetOrderNumber && (
                      <span className="text-[11px] font-mono font-bold text-amber-900 bg-amber-100/90 border border-amber-200/80 px-2.5 py-1 rounded-md">
                        Order #{targetOrderNumber}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* ─ RETURN SPECIFICATION METRICS ─ */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block mb-0.5">
                Return Type
              </span>
              <span className="text-xs font-extrabold text-[#772037]">
                {isPartial ? "Partial Return" : "Full Order Return"}
              </span>
            </div>

            <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block mb-0.5">
                Packets to Return
              </span>
              <span className="text-xs font-extrabold text-gray-800">
                {packetsToReturn} {packetsToReturn !== "—" ? "Packets" : ""}
                {weightPerPack ? ` (${weightPerPack} ${unitOfWeight}/pack)` : ""}
              </span>
            </div>

            <div
              onClick={handleNavigateToReturnDispatch}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleNavigateToReturnDispatch();
                }
              }}
              title="Click to navigate directly to Return Dispatch section"
              className="col-span-2 sm:col-span-1 p-3 rounded-xl bg-gray-50 hover:bg-emerald-50/70 border border-gray-100 hover:border-emerald-300 transition-all duration-200 cursor-pointer group shadow-none hover:shadow-sm"
            >
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 group-hover:text-emerald-700 transition-colors">
                  Decision Status
                </span>
                <span className="text-[10px] text-emerald-600 opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity font-semibold">
                  Open <FiExternalLink className="text-[9px]" />
                </span>
              </div>
              <span className="text-xs font-extrabold text-emerald-600 flex items-center gap-1 group-hover:text-emerald-700 transition-colors">
                <MdOutlineVerified className="text-emerald-500 flex-shrink-0" />
                <span className="truncate">
                  {returnDoc.returnDecision?.status?.replace(/_/g, " ") || "Return Request Approved"}
                </span>
              </span>
            </div>
          </div>

          {/* ─ BOTTOM ACTION CTA TO GO TO RETURN DISPATCH ─ */}
          {!isDispatched && (
            <div className="pt-1">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-gradient-to-r from-gray-50 via-amber-50/30 to-[#772037]/5 p-4 rounded-xl border border-gray-200/80">
                <div className="text-xs text-gray-600">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-gray-800">Ready to dispatch return items?</p>
                    {targetOrderNumber && (
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-gray-200/80 text-gray-700 font-bold">
                        Order #{targetOrderNumber}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Click to go directly to the Return Dispatch section to initiate dispatch and notify the supplier.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleNavigateToReturnDispatch}
                  className="w-full sm:w-auto flex-shrink-0 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#772037] to-[#CC234F] text-white font-bold text-xs hover:opacity-95 active:scale-98 transition shadow-md shadow-[#772037]/20 group cursor-pointer"
                >
                  <FiTruck className="text-sm" />
                  <span>Go to Return Dispatch</span>
                  <FiExternalLink className="text-xs group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>
              </div>
            </div>
          )}

          {/* ─ ADDRESS DETAILS (PICKUP & DESTINATION) ─ */}
          {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div className="p-3.5 rounded-xl bg-gray-50/70 border border-gray-100">
              <div className="flex items-center gap-1.5 text-gray-500 font-bold mb-1.5">
                <FiMapPin className="text-[#772037]" />
                <span className="text-[10px] uppercase tracking-wider">Pickup From (Your Location)</span>
              </div>
              <p className="text-gray-700 leading-relaxed font-medium">
                {formatAddress(pickupAddr)}
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-gray-50/70 border border-gray-100">
              <div className="flex items-center gap-1.5 text-gray-500 font-bold mb-1.5">
                <FiTruck className="text-emerald-600" />
                <span className="text-[10px] uppercase tracking-wider">Deliver To (Supplier Warehouse)</span>
              </div>
              <p className="text-gray-700 leading-relaxed font-medium">
                {formatAddress(deliveryAddr)}
              </p>
            </div>
          </div> */}

          {/* ─ DISPATCH TRACKING TIMELINE (When Dispatched) ─ */}
          {/* {isDispatched && (
            <div className="border border-gray-100 rounded-2xl p-4 bg-gray-50/40 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                  <MdOutlineLocalShipping className="text-[#772037] text-base" />
                  Shipment Tracking Status
                </span>
                {!showEditTracking && (
                  <button
                    onClick={() => setShowEditTracking(true)}
                    className="text-xs text-[#772037] hover:text-[#CC234F] font-bold flex items-center gap-1 transition"
                  >
                    <FiEdit3 /> Edit Tracking
                  </button>
                )}
              </div>

             
              <div className="relative flex justify-between items-start pt-2 px-2">
                {getReturnTrackingSteps(dispatchStatus).map((step, idx, arr) => (
                  <div key={idx} className="flex-1 flex flex-col items-center relative">
                    {idx !== 0 && (
                      <div className="absolute left-[-50%] top-3.5 w-full h-1 overflow-hidden">
                        <div
                          className={`h-full transition-all duration-700 ${step.completed
                              ? "bg-gradient-to-r from-emerald-400 to-emerald-600"
                              : "bg-gray-200"
                            }`}
                        />
                      </div>
                    )}
                    <div
                      className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold z-10 transition-all duration-500 relative ${step.completed
                          ? "bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-md shadow-emerald-400/40 scale-105"
                          : "bg-gray-200 text-gray-500"
                        }`}
                    >
                      {step.completed ? <FiCheck /> : idx + 1}
                    </div>
                    <p
                      className={`text-[10px] sm:text-xs mt-2 text-center leading-tight px-1 font-semibold ${step.completed ? "text-emerald-700" : "text-gray-400"
                        }`}
                    >
                      {step.label}
                    </p>
                  </div>
                ))}
              </div>

           
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-gray-200/60 text-xs">
                {returnDoc.returnDispatch?.dispatchedAt && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                    <span className="text-gray-400">Dispatched:</span>
                    <span className="font-semibold text-gray-800">
                      {fmtDateTime(returnDoc.returnDispatch.dispatchedAt)}
                    </span>
                  </div>
                )}
                {returnDoc.returnDispatch?.deliveredAt && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                    <span className="text-gray-400">Delivered:</span>
                    <span className="font-semibold text-gray-800">
                      {fmtDateTime(returnDoc.returnDispatch.deliveredAt)}
                    </span>
                  </div>
                )}
              </div>

          
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 pt-2">
                {returnDoc.returnDispatch?.AWB_Number ? (
                  <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-blue-50/80 border border-blue-100 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600 text-sm">📦</span>
                      <div>
                        <span className="text-[10px] font-bold text-blue-500 uppercase block leading-none">
                          AWB / Consignment
                        </span>
                        <span className="text-xs font-mono font-bold text-blue-950">
                          {returnDoc.returnDispatch.AWB_Number}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCopyAWB(returnDoc.returnDispatch.AWB_Number)}
                      className="p-1.5 rounded-lg bg-white border border-blue-200 text-blue-700 hover:bg-blue-100 transition"
                      title="Copy AWB"
                    >
                      {copiedAWB ? <FiCheck className="text-emerald-600" /> : <FiCopy />}
                    </button>
                  </div>
                ) : (
                  <div className="text-xs text-gray-400 italic">No AWB number provided</div>
                )}

                {returnDoc.returnDispatch?.trackingUrl && (
                  <a
                    href={
                      returnDoc.returnDispatch.trackingUrl.startsWith("http")
                        ? returnDoc.returnDispatch.trackingUrl
                        : `https://${returnDoc.returnDispatch.trackingUrl}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-xs hover:bg-emerald-700 transition shadow-sm"
                  >
                    <span>Track Package</span>
                    <FiExternalLink />
                  </a>
                )}
              </div>
            </div>
          )} */}

          {/* ─ EDIT TRACKING FORM (Optional update if already dispatched) ─ */}
          {/* {isDispatched && showEditTracking && (
            <div className="p-4 rounded-xl border border-gray-200 bg-white space-y-3">
              <h5 className="text-xs font-bold text-gray-700">Update Tracking Details</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                    AWB / Consignment Number
                  </label>
                  <input
                    type="text"
                    value={awbNumber}
                    onChange={(e) => setAwbNumber(e.target.value)}
                    placeholder="e.g. DEL-10294829"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:ring-2 focus:ring-[#772037]/20 focus:border-[#772037] outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                    Courier Tracking URL
                  </label>
                  <input
                    type="text"
                    value={trackingUrl}
                    onChange={(e) => setTrackingUrl(e.target.value)}
                    placeholder="https://track.courier.com/..."
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:ring-2 focus:ring-[#772037]/20 focus:border-[#772037] outline-none"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowEditTracking(false)}
                  className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
                <button
                  disabled={updatingTracking}
                  onClick={handleUpdateTracking}
                  className="px-4 py-1.5 rounded-lg bg-[#772037] text-white text-xs font-bold hover:bg-[#CC234F] disabled:opacity-50 transition"
                >
                  {updatingTracking ? "Saving..." : "Save Tracking"}
                </button>
              </div>
            </div>
          )} */}

          {/* ─ DISPATCH INPUTS & CTA (When NOT yet dispatched) ─ */}
          {/* {!isDispatched && (
            <div className="space-y-4 pt-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider block mb-1">
                    AWB / Consignment Number{" "}
                    <span className="text-gray-400 font-normal lowercase">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={awbNumber}
                    onChange={(e) => setAwbNumber(e.target.value)}
                    placeholder="e.g. AWB-98231024"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-xs font-mono focus:ring-2 focus:ring-[#772037]/20 focus:border-[#772037] outline-none transition"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider block mb-1">
                    Tracking Link / URL{" "}
                    <span className="text-gray-400 font-normal lowercase">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={trackingUrl}
                    onChange={(e) => setTrackingUrl(e.target.value)}
                    placeholder="https://track.courier.com/..."
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-xs focus:ring-2 focus:ring-[#772037]/20 focus:border-[#772037] outline-none transition"
                  />
                </div>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-3 bg-gradient-to-r from-gray-50 to-[#772037]/5 p-4 rounded-xl border border-gray-100">
                <div className="text-xs text-gray-600">
                  <p className="font-bold text-gray-800">Ready to dispatch return items?</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Clicking dispatch marks the return shipment as initiated and alerts the admin team.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setConfirmModalOpen(true)}
                  disabled={submitting}
                  className="w-full sm:w-auto flex-shrink-0 flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#772037] to-[#CC234F] text-white font-bold text-sm hover:opacity-95 active:scale-98 transition shadow-lg shadow-[#772037]/25"
                >
                  <FiTruck className="text-base" />
                  <span>
                    {isPartial ? "🚀 Dispatch Partial Return" : "🚀 Dispatch Return Order"}
                  </span>
                </button>
              </div>
            </div>
          )} */}
        </div>
      </div>

      {/* ══ CONFIRMATION MODAL ══ */}
      <ConfirmActionModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDispatch}
        title={isPartial ? "Confirm Partial Return Dispatch" : "Confirm Return Dispatch"}
        description={`Are you sure you want to mark this ${isPartial ? "partial return" : "return order"
          } as dispatched? Make sure the package (${packetsToReturn} packets) is ready for pickup or handed over to the logistics partner.`}
        confirmText="Yes, Dispatch Now"
        loading={submitting}
      />
    </>
  );
};

export default ReturnDispatchCard;
